import { useEffect } from 'react';
import { useLocation } from 'react-router';
import type { Locale } from '@et/schema';
import { LOCALES, swapLocaleInPath, toUrlSegment } from '../lib/locale.js';

/**
 * §16 §4 — `<html lang>` e os `hreflang` alternativos.
 *
 * A app é uma SPA, por isso isto não pode viver no `index.html`: o locale só
 * se conhece depois de resolver a rota, e muda sem recarregar a página. As
 * ligações são geradas a partir do caminho actual, o que garante que apontam
 * para **esta** página nas outras línguas e não para a raiz de cada uma —
 * apontar para a raiz é o erro de hreflang mais comum, e transforma uma
 * alternativa útil num redireccionamento inútil.
 */
export function DocumentLocale({ locale }: { readonly locale: Locale }) {
  const { pathname, search } = useLocation();

  useEffect(() => {
    document.documentElement.lang = toUrlSegment(locale);

    const head = document.head;
    for (const stale of head.querySelectorAll('link[data-et-hreflang]')) stale.remove();

    const origin = window.location.origin;
    const add = (hreflang: string, href: string) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = hreflang;
      link.href = href;
      link.dataset['etHreflang'] = 'true';
      head.appendChild(link);
    };

    for (const other of LOCALES) {
      add(toUrlSegment(other), `${origin}${swapLocaleInPath(pathname, other)}${search}`);
    }
    // `x-default` aponta para a canónica: é o que os motores servem a quem não
    // declara preferência, e deixá-lo por definir fá-los escolher por conta.
    add('x-default', `${origin}${swapLocaleInPath(pathname, 'en')}${search}`);

    let canonical = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical === null) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      head.appendChild(canonical);
    }
    canonical.href = `${origin}${pathname}${search}`;
  }, [locale, pathname, search]);

  return null;
}
